import LoadingButton from "@mui/lab/LoadingButton"
import Collapse from "@mui/material/Collapse"
import Paper from "@mui/material/Paper"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"
import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import Row from "../../../common/components/Row"
import { selectProfile } from "../../users/reducer"
import BorderedGrayChip from "../components/BorderedGrayChip"
import { retrieveCRMSettings, selectCRMSettings } from "../reducer"

const GIVE_ME_A_CALL = "Give Me A Call"

export default function VerifyCallPhoneSection({ onStatusMessage }) {
  const [phone, setPhone] = useState("")
  const [error, setError] = useState(null)
  const [validationCode, setValidationCode] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [primaryButtonText, setPrimaryButtonText] = useState(GIVE_ME_A_CALL)
  const [confirmingCallPhone, setConfirmingCallPhone] = useState(false)
  const confirmationAttempts = useRef(0)
  const profile = useSelector(selectProfile)
  const crmSettings = useSelector(selectCRMSettings)
  const dispatch = useDispatch()
  const axios = useAuthorizedAxiosClient()

  function handlePhoneChanged(e) {
    setPhone(e.target.value)
    setError(null)
  }

  function handleVerifyButtonClicked() {
    if (primaryButtonText === GIVE_ME_A_CALL) {
      setIsLoading(true)
      axios
        .post("/crm/api/twilio/send-caller-phone-verification/", { phone })
        .then(({ data }) => {
          setValidationCode(data.code)
          setPrimaryButtonText("I Finished The Call")
        })
        .catch(({ response: { data } }) => {
          if (data.phone) {
            setError(data.phone)
          } else if (data.code) {
            setError(data.message)
          }
        })
        .finally(() => setIsLoading(false))
    } else {
      onStatusMessage?.("Confirming phone verification...")
      setConfirmingCallPhone(true)
    }
  }

  useEffect(() => {
    setPhone(profile.phone || "")
  }, [profile.phone])

  useEffect(
    () => {
      let timeout
      if (confirmingCallPhone) {
        timeout = setTimeout(() => {
          if (
            !crmSettings.settings.twilio_call_phone &&
            confirmationAttempts.current < 5
          ) {
            dispatch(retrieveCRMSettings())
            confirmationAttempts.current += 1
          } else {
            if (crmSettings.settings.twilio_call_phone) {
              onStatusMessage?.("Phone successfully verified!")
              setPrimaryButtonText(GIVE_ME_A_CALL)
              setValidationCode("")
            } else {
              onStatusMessage?.(
                "Confirmation failed. You may need to refresh the page."
              )
            }
            setConfirmingCallPhone(false)
            confirmationAttempts.current = 0
          }
        }, 5000)
      }
      return () => {
        if (timeout) {
          clearTimeout(timeout)
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [crmSettings, confirmingCallPhone]
  )

  return (
    <Paper variant="outlined" sx={{ p: 2, width: "100%" }}>
      <Column sx={{ gap: 2, alignItems: "stretch" }}>
        <Typography variant="body1" sx={{ textAlign: "left" }}>
          To call candidates from the app, verify a caller ID number. You will
          receive a call and enter the code shown below.
        </Typography>
        <TextField
          label="Phone"
          value={phone}
          onChange={handlePhoneChanged}
          error={!!error}
          helperText={error}
          fullWidth
        />
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
            textAlign: "left",
            fontSize: "11px",
          }}
        >
          Validation code will appear here
        </Typography>
        <Collapse in={!!validationCode} sx={{ alignSelf: "flex-start" }}>
          <ValidationCodeChip label={validationCode} />
        </Collapse>
        <Row sx={{ justifyContent: "flex-start", gap: 1 }}>
          <LoadingButton
            variant="contained"
            color="primary"
            disabled={!phone || !!error}
            loading={isLoading}
            onClick={handleVerifyButtonClicked}
          >
            {primaryButtonText}
          </LoadingButton>
        </Row>
      </Column>
    </Paper>
  )
}

const ValidationCodeChip = styled(BorderedGrayChip)(({ theme }) => ({
  height: "68px",
  "& .MuiChip-label": {
    fontSize: "4em",
    letterSpacing: 8,
    paddingInline: theme.spacing(4),
  },
}))
